import React from 'react';
import XRegExp from 'xregexp';
import ModelProxy, {required, optional} from './proxy';
import {transliterate} from 'transliteration';

const lastnamePattern = XRegExp("^(Ma?c)?\\p{Lu}[\\p{Ll}]*(['-]\\p{Lu}[\\p{Ll}]*)*$");


export class Author extends ModelProxy {

  static propTypes = {
    uuid: required,
    lastname: required,
    initials: required
  };

  static setLastname(self, value) {
    self.props.lastname = transliterate(value);
  }

  static setInitials(self, value) {
    self.props.initials = transliterate(value);
  }

  static validateLastname(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else {
      const values = value.split(' ');
      for (const v of values) {
        if (!lastnamePattern.test(v)) {
          msgs.push({
            text: (
              `Please rewrite the lastname "${value}" in recommended ` +
              'format, for example: "Bartolo", "Dia Badiane".'
            ),
            level: 'warning'
          });
          break;
        }
      }
    }
    return msgs;
  }

  static validateInitials(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else if (value !== value.toUpperCase()) {
      msgs.push({
        text: (
          `Please rewrite the initial "${value}" in recomended ` +
          'format, for example: "AB".'
        ),
        level: 'warning'
      });
    }
    return msgs;
  }

}


export default class Reference extends ModelProxy {

  static propTypes = {
    id: optional,
    uuid: required,
    title: required,
    journal: required,
    refYear: required,
    medlineId: optional,
    published: optional,
    author: required,
    authors: required,
    nonMedlineDesc: optional,
    dataSource: optional,
    isNewJournal: required,
    loadFrom: required,
  };

  static defaultProps = {
    published: true,
    isNewJournal: false,
    loadFrom: '',
  };

  static setId(self, value) {
    if (typeof(value) === 'string' && !/^\d+$/.test(value)) {
      // process graphql's ID
      value = atob(value).replace(/^Reference:/, '');
    }
    if (value) {
      self.props.id = parseInt(value);
    }
  }

  static setAuthors(self, authors) {
    self.props.authors = authors.map(a => (
      a instanceof Author ? a : new Author(a)
    ));
  }

  static getAuthor(self) {
    if (!self.props.authors || self.props.authors.length === 0) {
      return null;
    }
    const [{lastname, initials}] = self.props.authors;
    return `${lastname}, ${initials}`;
  }

  static getRefName(self) {
    const {refYear, medlineId} = self.props;
    if (medlineId) {
      return `pmid-${medlineId}`;
    }
    const author = this.getAuthor(self);
    let refName = [];
    if (author) {
      refName.push((author || '').replace(/[\W]+/g, '-'));
    }
    if (refYear) {
      refName.push(refYear);
    }
    refName = refName.join('-');
    return refName.toLowerCase();
  }

  static getHumanRefName(self) {
    const {refYear, medlineId} = self.props;
    const author = this.getAuthor(self);
    let refName = [];
    if (author) {
      refName.push((author || '').replace(/[\W]+/g, '-'));
    }
    if (refYear) {
      refName.push(refYear);
    }
    if (medlineId) {
      refName.push(`pmid-${medlineId}`);
    }
    refName = refName.join('-');
    return refName.toLowerCase();
  }

  static setTitle(self, value) {
    self.props.title = transliterate(value);
  }

  static validateTitle(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else {
      if (!value.endsWith('.')) {
        msgs.push({
          text: 'Reference title should end with a period dot.',
          level: 'warning'
        });
      }
      if (!/^['"]?[A-Z]/.test(value)) {
        msgs.push({
          text: 'The first word of reference title should be capitalized.',
          level: 'warning'
        });
      }
    }
    return msgs;
  }

  static validateJournal(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else if (self.props.isNewJournal) {
      msgs.push({
        text: (
          <span>
            Journal "{value}" doesn't exist in database.{' '}
            Please send a email to <a href="mailto:hivdbteam@stanford.edu">HIVDB team</a>.
          </span>
        ),
        level: 'error'
      });
    }
    return msgs;
  }

  static validateRefYear(self, value) {
    const msgs = [];
    if (!value) {
      return msgs;
    }
    else {
      if (!/^\d+$/.test(value)) {
        msgs.push({
          text: `Publish year "${value}" contains character(s) other than digits.`,
          level: 'error'
        });
      }
      const year = parseInt(value);
      if (year < 1980) {
        msgs.push({
          text: `Publish year "${value}" is earlier than 1980.`,
          level: 'warning'
        });
      }
      const curYear = new Date().getFullYear();
      if (year > curYear) {
        msgs.push({
          text: `Publish year "${value}" is later than ${curYear}.`,
          level: 'error'
        });
      }
    }
    return msgs;
  }

  static validateMedlineId(self, value) {
    const msgs = [];
    if (value && !/^\d+$/.test(value)) {
      msgs.push({
        text: `Medline ID "${value}" contains character(s) other than digits.`,
        level: 'error'
      });
    }
    if (!value && !self.props.nonMedlineDesc) {
      msgs.push({
        text: 'Please provide where the data source and the URL',
        level: 'error'
      });
    }
    return msgs;
  }

  static validateNonMedlineDesc(self, value) {
    const msgs = [];
    if (value && self.props.medlineId) {
      msgs.push({
        text: `Non medline description will be ignored since property "medlineId" is not empty.`,
        level: 'warning'
      });
    }
    return msgs;
  }

  static validateAuthors(self, authors) {
    let msgs = [];
    if (!authors || authors.length === 0) {
      return msgs;
    }
    else {
      for (const author of authors) {
        author.validate();
        msgs = msgs.concat(author.messages());
      }
    }
    return msgs;
  }

}



// WEBPACK FOOTER //
// ./src/models/reference.js